import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import { Alert, Button, IconButton, Tab, Tabs } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Loading } from "../design-system";
import BuscarEquipo from "../components/BuscarEquipo";
import TeamStatisticsPdfExport from "../components/teamStatistics/TeamStatisticsPdfExport";
import TeamStatisticsHeader from "../components/teamStatistics/TeamStatisticsHeader";
import TeamStatisticsOverview from "../components/teamStatistics/TeamStatisticsOverview";
import TeamStatisticsDiscipline from "../components/teamStatistics/TeamStatisticsDiscipline";
import AdvancedStatistics from "../components/advancedStatistics/AdvancedStatistics";
import TeamStatisticsPlayers from "../components/teamStatistics/TeamStatisticsPlayers";
import ReportGoalStatistics from "../components/reports/ReportGoalStatistics";
import ReportGlobalStatistics from "../components/reports/ReportGlobalStatistics";
import TeamStatisticsMatches from "../components/teamStatistics/TeamStatisticsMatches";
import { idTemporada } from "../config";
import { useAppContext } from "../context/AppContext";
import { useClasificacion, useEquipoById, useEquipoEstadisticasById, useJornadas } from "../hooks/queries";
import usePageTitle from "../hooks/usePageTitle";
import usePageTour from "../hooks/usePageTour";
import { getMatchdayNumber } from "../utils/matchdays";
import "./InformePartidoPageModern.css";
import "./EstadisticasEquiposPageModern.css";

const SECTIONS = ['summary', 'advanced', 'players', 'goals', 'discipline', 'globalStats', 'matches'];

export const getTeamContextLabels = ({
  filters,
  effectiveTemporada,
  equipo,
  estadisticas,
}) => {
  const seasonMatches = String(filters?.temporada ?? "") === String(effectiveTemporada ?? "");
  const competitionMatches = String(filters?.competicion ?? "") === String(estadisticas?.idCompeticion ?? "");

  return {
    seasonLabel: (seasonMatches ? filters?.temporadaNombre : null)
      || equipo?.temporadaNombre
      || effectiveTemporada,
    competitionLabel: (seasonMatches && competitionMatches ? filters?.competicionNombre : null)
      || equipo?.nombreCompeticion
      || estadisticas?.nombreCompeticion,
  };
};

const EstadisticasEquiposPageModern = () => {
  const { t } = useTranslation("stats");
  usePageTitle(t('teams.headerTitle'));
  usePageTour("estadisticasEquipos");
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [queryParams, setQueryParams] = useSearchParams();
  const id = queryParams.get("id");
  const queryTemporada = queryParams.get("idTemporada");
  const effectiveTemporada = queryTemporada || (id ? id.split("_").pop() : null) || idTemporada;
  const { setEquipoSeleccionado, setEstadisticasEquipo, filters, setFilters } = useAppContext();

  const { data: estadisticas, isLoading: loadingStats, isError: statsError, refetch: refetchStats } = useEquipoEstadisticasById(effectiveTemporada, id, { enabled: !!id });
  const sections = SECTIONS.filter(key => key !== 'advanced' || estadisticas?.estadisticasAvanzadas);
  const section = sections.includes(queryParams.get('section')) ? queryParams.get('section') : 'summary';
  const { data: equipo, isLoading: loadingTeam, isError: teamError, refetch: refetchTeam } = useEquipoById(effectiveTemporada, estadisticas?.idEquipo, { enabled: !!(id && estadisticas?.idEquipo) });
  const { data: clasificacionData, isLoading: loadingClassification, isError: classificationError, refetch: refetchClasificacion } = useClasificacion(effectiveTemporada, estadisticas?.idCompeticion, estadisticas?.idGrupo, { enabled: !!(id && estadisticas?.idGrupo) });
  const { data: jornadasData, isLoading: loadingCalendar, isError: calendarError, refetch: refetchJornadas } = useJornadas(estadisticas?.idGrupo, effectiveTemporada, { enabled: !!(id && estadisticas?.idGrupo) });
  const loading = !!id && (loadingStats || loadingTeam);
  const error = statsError || teamError;

  const { standing: clasificacionEquipo, round: classificationRound } = useMemo(() => {
    if (!estadisticas || !Array.isArray(clasificacionData) || !clasificacionData.length) return {};
    const latest = [...clasificacionData].filter(round => round.clasificacion?.length)
      .sort((a, b) => (getMatchdayNumber(a) ?? -1) - (getMatchdayNumber(b) ?? -1)).at(-1);
    return { standing: latest?.clasificacion.find(item => String(item.codequipo) === String(estadisticas.idEquipo)) || null,
      round: latest ? getMatchdayNumber(latest) : null };
  }, [clasificacionData, estadisticas]);
  const jornadas = useMemo(() => jornadasData?.jornadas || [], [jornadasData]);
  const statistics = useMemo(() => estadisticas ? {
    ...estadisticas, partidos: estadisticas.partidos || {}, datos_extra: estadisticas.datos_extra || {},
    goles: {
      ...estadisticas.goles,
      ...Object.fromEntries(['golesTotales', 'golesTotalesPenalti', 'golesTotalesPropia',
        'golesTotalesRecibidos', 'golesTotalesRecibidosPenalti', 'golesTotalesRecibidosPropia']
        .map(key => [key, estadisticas.goles?.[key] ?? 0])),
    },
  } : null, [estadisticas]);
  const team = estadisticas ? {
    ...equipo, id: estadisticas.idEquipo, idEquipo: estadisticas.idEquipo,
    nombre: equipo?.nombre || clasificacionEquipo?.nombre || estadisticas.nombre,
    idGrupo: estadisticas.idGrupo, idTemporada: effectiveTemporada, idCompeticion: estadisticas.idCompeticion,
  } : null;
  const { seasonLabel, competitionLabel } = getTeamContextLabels({
    filters, effectiveTemporada, equipo, estadisticas,
  });
  const groupLabel = String(filters.grupo) === String(estadisticas?.idGrupo) &&
    String(filters.temporada) === String(effectiveTemporada) ? filters.grupoNombre : estadisticas?.nombreGrupo;

  useEffect(() => {
    if (!id) {
      setEstadisticasEquipo(null);
      setEquipoSeleccionado(null);
      return;
    }
    setEstadisticasEquipo(estadisticas || null);
  }, [estadisticas, id, setEquipoSeleccionado, setEstadisticasEquipo]);

  useEffect(() => {
    if (!estadisticas || !equipo) return;
    setEquipoSeleccionado({ ...equipo, idGrupo: estadisticas.idGrupo, idTemporada: effectiveTemporada, idCompeticion: estadisticas.idCompeticion });
  }, [effectiveTemporada, equipo, estadisticas, setEquipoSeleccionado]);

  useEffect(() => { localStorage.setItem("jornadas", JSON.stringify(jornadas)); }, [jornadas]);

  const searchAnotherTeam = () => {
    setEstadisticasEquipo(null);
    setEquipoSeleccionado(null);
    navigate("/estadisticasEquipos");
  };
  const handleEquipoSeleccionado = (season, competition, group, team) => navigate(`/estadisticasEquipos?id=${team}_${competition}_${group}_${season}&idTemporada=${season}`);
  const handleRetry = () => { refetchStats(); refetchTeam(); refetchClasificacion(); refetchJornadas(); };
  const classificationParams = new URLSearchParams({ team: String(estadisticas?.idEquipo ?? '') });
  if (classificationRound != null) classificationParams.set('jornada', String(classificationRound));
  const classificationTo = estadisticas?.idCompeticion && estadisticas?.idGrupo
    ? `/clasificacion?${classificationParams}` : null;
  const openClassification = () => setFilters(previous => ({
    ...previous, temporada: effectiveTemporada, temporadaNombre: seasonLabel,
    competicion: estadisticas.idCompeticion, competicionNombre: competitionLabel || '',
    grupo: estadisticas.idGrupo, grupoNombre: groupLabel || '',
    ...(String(previous.temporada) !== String(effectiveTemporada)
      || String(previous.competicion) !== String(estadisticas.idCompeticion)
      ? { federacion: null, federacionNombre: '', categoria: null, categoriaNombre: '' } : {}),
  }));

  const changeSection = (_, value) => {
    const next = new URLSearchParams(queryParams);
    if (value === 'summary') next.delete('section');
    else next.set('section', value);
    setQueryParams(next, { state: location.state, preventScrollReset: true });
  };

  if (loading) return <div className="rr-report ts-page ts-loading" data-theme={theme.palette.mode}>
    <Loading variant="soccer" size="large" />
  </div>;

  return <div className="rr-report ts-page" data-theme={theme.palette.mode}>
    {error && <Alert severity="error" action={<Button onClick={handleRetry}>{t("teams.errors.retry")}</Button>}>
      {t("teams.errors.loadData")}</Alert>}
    <header className="ts-toolbar" data-tour="team-stats-header">
      <div><IconButton onClick={() => navigate(location.state?.directoryFrom ? -1 : "/dashboard")}
        aria-label={t("teams.backAriaLabel")}><ArrowBack /></IconButton>
        {estadisticas ? <span>{t("teams.headerTitle")}</span> : <h1>{t("teams.headerTitle")}</h1>}</div>
      {estadisticas && <div className="ts-actions" data-tour="team-stats-export">
        <TeamStatisticsPdfExport key={`${effectiveTemporada}:${id}`} team={team} statistics={statistics}
          standing={clasificacionEquipo} calendar={jornadas} season={seasonLabel} competition={competitionLabel}
          group={groupLabel} disabled={loadingClassification || loadingCalendar || error}
          incomplete={classificationError || calendarError} />
        <Button variant="outlined" onClick={searchAnotherTeam}>{t("teams.searchAnotherTeam")}</Button>
      </div>}
    </header>
    <div>
      {estadisticas ? <>
        <TeamStatisticsHeader team={team} statistics={statistics} standing={clasificacionEquipo}
          calendar={jornadas} season={seasonLabel} competition={competitionLabel}
          group={groupLabel} classificationTo={classificationTo} onClassificationClick={openClassification} />
        {(classificationError || calendarError) && <Alert severity="warning" action={<Button onClick={handleRetry}>
          {t('teams.errors.retry')}</Button>}>{t('teams.modern.partialData')}</Alert>}
        <Tabs className="rr-tabs ts-tabs" data-tour="team-stats-tabs" value={section} onChange={changeSection}
          variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label={t('teams.modern.sections')}>
          {sections.map(key => <Tab key={key} value={key} id={`ts-tab-${key}`}
            aria-controls={`ts-panel-${key}`} label={t(key === 'advanced' ? 'advanced:title' : `teams.tabs.${key}`)} />)}
        </Tabs>
        <div className="ts-panel" role="tabpanel" tabIndex={0} id={`ts-panel-${section}`} aria-labelledby={`ts-tab-${section}`}>
          {section === 'summary' && <TeamStatisticsOverview team={team} statistics={statistics}
            standing={clasificacionEquipo} calendar={jornadas} />}
          {section === 'advanced' && <AdvancedStatistics key={`${effectiveTemporada}:${id}`} kind="team"
            record={{ ...estadisticas, idTemporada: effectiveTemporada }} />}
          {section === 'players' && <TeamStatisticsPlayers key={`${effectiveTemporada}:${id}`} team={team} />}
          {section === 'goals' && <ReportGoalStatistics estadisticas={statistics} clasificacion={clasificacionEquipo} missingValue="0" />}
          {section === 'discipline' && <TeamStatisticsDiscipline statistics={statistics} />}
          {section === 'globalStats' && <ReportGlobalStatistics estadisticas={statistics} clasificacion={clasificacionEquipo} missingValue="0" />}
          {section === 'matches' && <TeamStatisticsMatches team={team} calendar={jornadas} />}
        </div>
      </> : <section className="rr-panel ts-empty" data-tour="team-stats-search">
        <h2>{t("teams.emptyState.title")}</h2><p>{t("teams.emptyState.description")}</p>
        <BuscarEquipo onEquipoSeleccionado={handleEquipoSeleccionado} />
      </section>}
    </div>
  </div>;
};

export default EstadisticasEquiposPageModern;
